import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AimNote } from '../../models/AIMModels';
import { notesEndpoints } from '../endpoints/notesEndpoints';
import { AimNoteMutationParams, AimNoteQueryParams } from '../../models/APIModels';
import { AIM_SERVICE } from '../../envUtils';

const AimApiQueryCreator = () => {
  let getToken: () => Promise<string> | string = () => '';
  const setTokenGetter = (getter: () => Promise<string> | string) => {
    getToken = getter;
  };
  const api = createApi({
    reducerPath: 'notesApi',
    baseQuery: fetchBaseQuery({
      baseUrl: `${AIM_SERVICE}`,
      prepareHeaders: async (headers) => {
        const token = await getToken();
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
      }
    }),
    tagTypes: ['AimNote'],
    endpoints: (builder) => ({
      notes: builder.query<AimNote[], number>({
        query: (aimId) => notesEndpoints.notes(aimId),
        providesTags: (result, error, aimId) => [{ type: 'AimNote', aimId }]
      }),
      noteDetails: builder.query<AimNote, AimNoteQueryParams>({
        query: ({ aimId, noteId }) => notesEndpoints.noteDetails(aimId, noteId),
        providesTags: (result, error, noteId) => [{ type: 'AimNote', noteId }]
      }),
      newNote: builder.mutation<AimNote, AimNoteMutationParams>({
        query: ({ ...payload }) => ({
          url: notesEndpoints.newNote(payload.aimId),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['AimNote']
      }),
      updateNote: builder.mutation<AimNote, AimNoteMutationParams>({
        query: ({ ...payload }) => ({
          url: notesEndpoints.updateNote(payload.aimId, payload.noteId),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['AimNote']
      }),
      deleteNote: builder.mutation<AimNote, AimNoteQueryParams>({
        query: ({ aimId, noteId }) => ({
          url: notesEndpoints.deleteNote(aimId, noteId),
          method: 'POST'
        }),
        invalidatesTags: ['AimNote']
      })
    })
  });
  return { hooks: api, setTokenGetter };
};

/**
 * An RTK Query service providing React hooks for notes CRUD operations in the AIM platform, built with `fetchBaseQuery` and automatic Bearer-token injection.
 */
export const NotesService = AimApiQueryCreator();
export const {
  useLazyNotesQuery,
  useLazyNoteDetailsQuery,
  useNewNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation
} = NotesService.hooks;
